import * as inflection from 'inflection';
import { Delta } from 'quill/core';
import React from 'react';
import * as styles from './PostResultCard.module.scss';
import { saveSearchContext } from './searchContext';
import Avatar from '../components/Avatar';
import AvatarStack from '../components/AvatarStack';
import Card, { CardSeparator } from '../components/Card';
import CardHeader from '../components/CardHeader';
import DeltaPreview from '../components/DeltaPreview';
import HoverPreviewable from '../components/HoverPreviewable';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import WrappingTwoColumnLayout from '../components/WrappingTwoColumnLayout';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import clockIcon from '../icons/non-prefilled/16x16/clock.svg';
import PostBadge from '../post/PostBadge';
import PostDetailPreview from '../post/PostDetailPreview';
import PostIcon from '../post/PostIcon';
import PostTitleWithIcons from '../post/PostTitleWithIcons';
import PostSeriesMeta from '../post/series/PostSeriesMeta';
import TopicPills from '../topic/TopicPills';
import UserDetailPreviewPopOver from '../user/UserDetailPreviewPopOver';
import addTestId from '../utils/addTestId';
import omitNonTopics from '../utils/omitNonTopics';
import isSize from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import Link from '../utils/router/Link';
import renderHighlight from '../utils/search/renderHighlight';
import postPath from '../utils/url/postPath';

const POST_FRAGMENT = gql(`
  fragment PostResultCard_post on Post {
    id
    views
    updatedAt
    publishedAt
    userViewedAt
    ...postPath_post
    ...PostBadge_post
    series {
      id
      ...PostSeriesMeta_series
    }
    ...PostIcon_post
    ...PostTitleWithIcons_post
    topics {
      ...TopicPills_topics
    }
    contributors {
      ...AvatarStack_users
    }
  }
`);

const ANCESTORS_FRAGMENT = gql(`
  fragment PostResultCard_ancestors on AncestorSearchResult {
    id
    name
  }
`);

interface PostResultCardProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  title?: string | null;
  content: Delta;
  queryId?: string;
  resultId?: string;
  position: number;
  highlight: Delta;
  highlightIndex?: number;
  ancestors?: DocumentType<typeof ANCESTORS_FRAGMENT>[];
}

const PostResultCard = ({
  post,
  content,
  title,
  queryId,
  resultId,
  position,
  highlight,
  highlightIndex,
  ancestors = [],
}: PostResultCardProps) => {
  const path = postPath(post);

  const isShowingViewedAtMetaOnBottom = isSize(Sizes.SM);

  const viewedAtMeta = post.userViewedAt && (
    <div className={styles.viewedMeta}>
      <SVGIcon inline size="small" className={styles.viewedAtIcon}>
        {clockIcon}
      </SVGIcon>{' '}
      You viewed <Timestamp date={post.userViewedAt} />
    </div>
  );

  const contributors = post.contributors || [];

  const hasBadge = !!post.archivedAt || post.publishedAt === null;
  const hasRight = contributors.length > 0 || hasBadge;
  const topics = omitNonTopics(post.topics ?? []);
  const hasFooter = topics.length > 0 || hasRight;

  const handleClick = () => {
    const searchHighlightOpIndex = highlight.ops.findIndex(
      (op) => op.attributes?.searchHighlight,
    );
    const highlightedRangeIndex =
      highlightIndex && searchHighlightOpIndex !== -1
        ? highlightIndex +
          new Delta(highlight.ops.slice(0, searchHighlightOpIndex)).length()
        : undefined;

    saveSearchContext(post.id, {
      position,
      queryId,
      resultId,
      highlightedRangeIndex,
    });
  };

  return (
    <Card to={path} onClick={handleClick}>
      <HoverPreviewable
        renderPreview={({ isOpen }) => (
          <PostDetailPreview
            postId={post.id}
            queryId={queryId}
            resultId={resultId}
            position={position}
            open={isOpen}
          />
        )}
      >
        {(targetProps) => (
          <CardHeader
            icon={
              <div className={styles.icon}>
                <PostIcon post={post} />
                <Link
                  to={path}
                  className={styles.hoverTarget}
                  aria-label={post.title}
                  {...addTestId('post-result-card-link')}
                  {...targetProps}
                />
              </div>
            }
            aboveTitle={
              post.series && (
                <div className={styles.seriesContainer}>
                  <PostSeriesMeta postId={post.id} series={post.series} />
                </div>
              )
            }
            title={
              <PostTitleWithIcons
                post={post}
                title={title && renderHighlight(title)}
              />
            }
            meta={
              'publishedAt' in post && (
                <>
                  <ul className={styles.metaList}>
                    {post.updatedAt ? (
                      <li>
                        Updated <Timestamp date={post.updatedAt} />
                      </li>
                    ) : null}
                    {post.views > 0 && (
                      <li>
                        {post.views} {inflection.inflect('view', post.views)}
                      </li>
                    )}
                  </ul>
                  {isShowingViewedAtMetaOnBottom && (
                    <div className={styles.bottomMeta}>{viewedAtMeta}</div>
                  )}
                </>
              )
            }
            rightMeta={!isShowingViewedAtMetaOnBottom && viewedAtMeta}
          />
        )}
      </HoverPreviewable>
      <CardSeparator />
      <DeltaPreview
        typography="searchResult"
        content={content}
        maxLines={8}
        link={false}
        additionalBlockEmbedHandlers={{
          image: (value, key, attributes) => {
            const caption =
              typeof attributes.searchHighlight === 'string'
                ? renderHighlight(attributes.searchHighlight)
                : attributes.caption;
            return DeltaPreview.embedHandlers.image(value, key, {
              ...attributes,
              caption,
            });
          },
        }}
      />
      {hasFooter && (
        <div className={styles.footer}>
          <WrappingTwoColumnLayout
            left={
              <TopicPills
                topics={topics}
                names={topics.map(
                  (topic) => ancestors.find((a) => a.id === topic.id)?.name,
                )}
                highlighted
                size="small"
              />
            }
            right={
              !!hasRight && (
                <div className={styles.right}>
                  <div className={styles.contributors}>
                    <AvatarStack items={contributors.slice(0, 3)}>
                      {(user, options) => (
                        <UserDetailPreviewPopOver
                          userId={user.id}
                          placement="top-start"
                        >
                          <Avatar user={user} size={options.size} />
                        </UserDetailPreviewPopOver>
                      )}
                    </AvatarStack>
                  </div>
                  <PostBadge post={post} inline={false} />
                </div>
              )
            }
          />
        </div>
      )}
    </Card>
  );
};

export default PostResultCard;
